import axios from 'axios';
import { Aes256Decrypt, Aes256Encrypt } from '@/Components/Common/Utility';

const encDecStatus = process.env.REACT_APP_ENC_DEC_STATUS;
const isEncDec = encDecStatus === 'true' || encDecStatus === true;

/** Parse API `data` whether it is a JSON string or already an object. */
function safeParseApiData(value) {
    if (value == null) return null;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }
    return null;
}

/** POST FRWNIBRSRequired and return parsed payload (handles string or object responses). */
async function postFrwNibrsRequiredApi(incidentId) {
    const postData = { IncidentID: incidentId };
    const ipAddress = sessionStorage.getItem('IPAddress');
    if (ipAddress) postData.IPAddress = ipAddress;

    if (isEncDec) {
        const encPostData = await Aes256Encrypt(JSON.stringify(postData));
        const res = await axios.post('IncidentValidation/FRWNIBRSRequired', { EDpostData: encPostData });
        const decryptedData = await Aes256Decrypt(res?.data?.data);
        return safeParseApiData(decryptedData);
    }

    const res = await axios.post('IncidentValidation/FRWNIBRSRequired', postData);
    return safeParseApiData(res?.data?.data) ?? safeParseApiData(res?.data);
}

/** Error codes returned by IncidentValidation/FRWNIBRSRequired that map to NIBRS sidebar links. */
export const FRW_NIBRS_ERROR_CODES = {
    VICTIM: 'Error_Victim_Required',
    SOCIETY_VICTIM: 'Error_SocietyVictim_Required',
    OFFENDER: 'Error_Offender_Required',
    PROPERTY: 'Error_Property_Required',
    DRUG_PROPERTY: 'Error_DrugProperty_Required',
    VEHICLE: 'Error_Vehicle_Required',
    ARREST: 'Error_Arrest_Required',
};

export const FRW_NIBRS_REQUIRED_ERROR_CODES = Object.values(FRW_NIBRS_ERROR_CODES);

const EMPTY_FRW_NIBRS_REQUIRED = {
    showNibrsSection: false,
    requireVictim: false,
    requireSocietyVictim: false,
    requireOffender: false,
    requireProperty: false,
    requireDrugProperty: false,
    requireVehicle: false,
    requireArrest: false,
    errorCodes: [],
    errorMsg: '',
};

/** Read ErrorCode array from API payload and keep only NIBRS link codes we understand. */
export function parseFrwNibrsRequiredErrorCodes(response = {}) {
    const codes = response?.ErrorCode;
    if (!Array.isArray(codes) || !codes.length) return [];
    return codes.filter((code) => FRW_NIBRS_REQUIRED_ERROR_CODES.includes(code));
}

/** Turn ErrorCode list into simple flags for StatusBar (which links are allowed to show). */
export function buildFrwNibrsRequiredFlags(errorCodes = [], errorMsg = '') {
    if (!Array.isArray(errorCodes) || !errorCodes.length) {
        return { ...EMPTY_FRW_NIBRS_REQUIRED, errorMsg: String(errorMsg || '') };
    }

    const codeSet = new Set(errorCodes);
    return {
        showNibrsSection: true,
        requireVictim: codeSet.has(FRW_NIBRS_ERROR_CODES.VICTIM),
        requireSocietyVictim: codeSet.has(FRW_NIBRS_ERROR_CODES.SOCIETY_VICTIM),
        requireOffender: codeSet.has(FRW_NIBRS_ERROR_CODES.OFFENDER),
        requireProperty: codeSet.has(FRW_NIBRS_ERROR_CODES.PROPERTY),
        requireDrugProperty: codeSet.has(FRW_NIBRS_ERROR_CODES.DRUG_PROPERTY),
        requireVehicle: codeSet.has(FRW_NIBRS_ERROR_CODES.VEHICLE),
        requireArrest: codeSet.has(FRW_NIBRS_ERROR_CODES.ARREST),
        errorCodes,
        errorMsg: String(errorMsg || ''),
    };
}

/** Unwrap API payload — supports flat object, Table array/object/string, or row array. */
function normalizeFrwNibrsRequiredPayload(raw) {
    if (raw == null || raw === '') return {};

    let payload = raw;

    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return {};
        }
    }

    if (Array.isArray(payload)) {
        const row = payload.find((item) => item && (item.ErrorCode != null || item.Code != null));
        return row || payload[0] || {};
    }

    // Standard API wrapper: { Table: [...] | {...} | "json string" }
    if (payload?.Table != null) {
        return normalizeFrwNibrsRequiredPayload(payload.Table);
    }

    return payload;
}

/**
 * Call IncidentValidation/FRWNIBRSRequired for the current incident.
 * Called once when the user selects a Report Template.
 */
export async function fetchFrwNibrsRequired(incidentId) {
    if (!incidentId) return { flags: { ...EMPTY_FRW_NIBRS_REQUIRED }, apiResponse: null };

    const raw = await postFrwNibrsRequiredApi(incidentId);
    const payload = normalizeFrwNibrsRequiredPayload(raw);
    const errorCodes = parseFrwNibrsRequiredErrorCodes(payload);
    const flags = buildFrwNibrsRequiredFlags(errorCodes, payload?.ErrorMsg || '');
    return { flags, apiResponse: payload };
}

export function getEmptyFrwNibrsRequiredFlags() {
    return { ...EMPTY_FRW_NIBRS_REQUIRED };
}

export function getFrwNibrsRequiredStorageKey(incidentId) {
    return `report-nibrs-frw-required-${incidentId}`;
}

/** Persist + broadcast FRW NIBRS required flags to StatusBar. */
export function dispatchFrwNibrsRequiredUpdate(incidentId, flags) {
    if (!incidentId) return;
    sessionStorage.setItem(getFrwNibrsRequiredStorageKey(incidentId), JSON.stringify(flags));
    window.dispatchEvent(new CustomEvent('report-nibrs-frw-required-updated', {
        detail: {
            incidentId,
            ...flags,
        },
    }));
}

export function readFrwNibrsRequiredFromStorage(incidentId) {
    if (!incidentId) return getEmptyFrwNibrsRequiredFlags();
    try {
        const raw = sessionStorage.getItem(getFrwNibrsRequiredStorageKey(incidentId));
        if (!raw) return getEmptyFrwNibrsRequiredFlags();
        const parsed = JSON.parse(raw);
        return buildFrwNibrsRequiredFlags(parsed?.errorCodes || [], parsed?.errorMsg || '');
    } catch {
        return getEmptyFrwNibrsRequiredFlags();
    }
}

// ✅ This function is used to read the presence state of the NIBRS data from the session storage.
function readNibrsPresenceState(incidentId) {
    if (!incidentId) {
        return {
            victim: false,
            societyVictim: false,
            offender: false,
            property: false,
            drugProperty: false,
            vehicle: false,
            arrest: false,
        };
    }

    return {
        victim: sessionStorage.getItem(`report-nibrs-victim-present-${incidentId}`) === 'true',
        societyVictim: sessionStorage.getItem(`report-nibrs-society-victim-present-${incidentId}`) === 'true',
        offender: sessionStorage.getItem(`report-nibrs-offender-present-${incidentId}`) === 'true',
        property: sessionStorage.getItem(`report-nibrs-property-present-${incidentId}`) === 'true',
        drugProperty: sessionStorage.getItem(`report-nibrs-drug-property-present-${incidentId}`) === 'true',
        vehicle: sessionStorage.getItem(`report-nibrs-vehicle-present-${incidentId}`) === 'true',
        arrest: sessionStorage.getItem(`report-nibrs-arrest-present-${incidentId}`) === 'true',
    };
}

// ✅ This function is used to derive the NIBRS readiness counts from the required and present states.
export function deriveNibrsReadinessCounts(required = {}, present = {}, totalCount = 0) {
    const isDrugPropertyResolved = Boolean(required.requireDrugProperty) && Boolean(present.drugProperty);
    const effectiveRequireProperty = Boolean(required.requireProperty) && !isDrugPropertyResolved;

    const derivedTotalCount = [
        Boolean(required.requireVictim) || totalCount > 0,
        Boolean(required.requireSocietyVictim) || totalCount > 0,
        Boolean(required.requireOffender) || totalCount > 0,
        effectiveRequireProperty || totalCount > 0,
        Boolean(required.requireDrugProperty) || totalCount > 0,
        Boolean(required.requireVehicle) || totalCount > 0,
        Boolean(required.requireArrest) || totalCount > 0,
    ].filter(Boolean).length;

    const unresolvedCount = [
        Boolean(required.requireVictim) && !Boolean(present.victim),
        Boolean(required.requireSocietyVictim) && !Boolean(present.societyVictim),
        Boolean(required.requireOffender) && !Boolean(present.offender),
        effectiveRequireProperty && !Boolean(present.property),
        Boolean(required.requireDrugProperty) && !Boolean(present.drugProperty),
        Boolean(required.requireVehicle) && !Boolean(present.vehicle),
        Boolean(required.requireArrest) && !Boolean(present.arrest),
    ].filter(Boolean).length;

    const resolvedCount = Math.max(totalCount - unresolvedCount, 0);
    return { resolvedCount, totalCount, unresolvedCount, effectiveRequireProperty };
}

// ✅ This function is used to calculate the NIBRS readiness percentage from the resolved and total counts.
export function calculateNibrsReadinessPercent(resolvedCount, totalCount) {
    const resolved = Number.parseInt(resolvedCount, 10);
    const total = Number.parseInt(totalCount, 10);
    if (!Number.isFinite(total) || total <= 0) return 0;
    const safeResolved = Number.isFinite(resolved) ? Math.max(0, Math.min(resolved, total)) : 0;
    return Math.round((safeResolved / total) * 100);
}

export function getNibrsReadinessCountsStorageKey(incidentId) {
    return `report-nibrs-readiness-counts-${incidentId}`;
}

// ✅ This function is used to dispatch the NIBRS readiness counts update to the session storage.
export function dispatchNibrsReadinessCountsUpdate(incidentId, counts = {}) {
    if (!incidentId) return;
    const resolvedCount = Number.parseInt(counts?.resolvedCount, 10);
    const totalCount = Number.parseInt(counts?.totalCount, 10);
    const payload = {
        incidentId,
        resolvedCount: Number.isFinite(resolvedCount) && resolvedCount >= 0 ? resolvedCount : 0,
        totalCount: Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : 0,
    };

    sessionStorage.setItem(getNibrsReadinessCountsStorageKey(incidentId), JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent('report-nibrs-readiness-counts-updated', {
        detail: payload,
    }));
}

// ✅ This function is used to read the NIBRS readiness counts snapshot from the session storage.
export function readNibrsReadinessCountsSnapshotFromStorage(incidentId) {
    if (!incidentId) return { resolvedCount: 0, totalCount: 0 };
    try {
        const raw = sessionStorage.getItem(getNibrsReadinessCountsStorageKey(incidentId));
        if (!raw) return { resolvedCount: 0, totalCount: 0 };
        const parsed = JSON.parse(raw);
        const resolvedCount = Number.parseInt(parsed?.resolvedCount, 10);
        const totalCount = Number.parseInt(parsed?.totalCount, 10);
        return {
            resolvedCount: Number.isFinite(resolvedCount) && resolvedCount >= 0 ? resolvedCount : 0,
            totalCount: Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : 0,
        };
    } catch {
        return { resolvedCount: 0, totalCount: 0 };
    }
}

/**
 * Build NIBRS readiness counters from the same inputs used by the sidebar card.
 * `resolvedCount` = fixed/resolved required entries, `totalCount` = all required entries.
 */
export function readNibrsReadinessCountsFromStorage(incidentId) {
    if (!incidentId) return { resolvedCount: 0, totalCount: 0, unresolvedCount: 0 };

    const required = readFrwNibrsRequiredFromStorage(incidentId);
    const present = readNibrsPresenceState(incidentId);
    return deriveNibrsReadinessCounts(required, present);
}

/** Remove all report-module session keys for an incident and notify StatusBar listeners. */
export function clearReportIncidentSessionStorage(incidentId) {
    if (!incidentId) return;

    const sessionKeys = [
        `report-name-suggestion-${incidentId}`,
        `report-property-suggestion-${incidentId}`,
        `report-vehicle-suggestion-${incidentId}`,
        `report-arrest-suggestion-${incidentId}`,
        `report-template-selected-${incidentId}`,
        `report-narrative-status-${incidentId}`,
        `report-nibrs-victim-present-${incidentId}`,
        `report-nibrs-society-victim-present-${incidentId}`,
        `report-nibrs-offender-present-${incidentId}`,
        `report-nibrs-property-present-${incidentId}`,
        `report-nibrs-drug-property-present-${incidentId}`,
        `report-nibrs-vehicle-present-${incidentId}`,
        `report-nibrs-arrest-present-${incidentId}`,
        getFrwNibrsRequiredStorageKey(incidentId),
        getNibrsReadinessCountsStorageKey(incidentId),
    ];
    sessionKeys.forEach((key) => sessionStorage.removeItem(key));

    dispatchFrwNibrsRequiredUpdate(incidentId, getEmptyFrwNibrsRequiredFlags());
    dispatchNibrsReadinessCountsUpdate(incidentId, { resolvedCount: 0, totalCount: 0 });

    window.dispatchEvent(new CustomEvent('report-name-suggestion-updated', {
        detail: {
            incidentId,
            hasNameSuggestion: false,
            hasPropertySuggestion: false,
            hasVehicleSuggestion: false,
            hasArrestSuggestion: false,
        },
    }));
    window.dispatchEvent(new CustomEvent('report-template-selection-updated', {
        detail: { incidentId, hasSelectedTemplate: false },
    }));
    window.dispatchEvent(new CustomEvent('report-narrative-status-updated', {
        detail: { incidentId, status: '' },
    }));
    window.dispatchEvent(new CustomEvent('report-nibrs-victim-presence-updated', {
        detail: { incidentId, hasVictimNamePresent: false },
    }));
    window.dispatchEvent(new CustomEvent('report-nibrs-society-victim-presence-updated', {
        detail: { incidentId, hasSocietyVictimNamePresent: false },
    }));
    window.dispatchEvent(new CustomEvent('report-nibrs-offender-presence-updated', {
        detail: { incidentId, hasOffenderNamePresent: false },
    }));
    window.dispatchEvent(new CustomEvent('report-nibrs-property-presence-updated', {
        detail: { incidentId, hasPropertyPresent: false },
    }));
    window.dispatchEvent(new CustomEvent('report-nibrs-drug-property-presence-updated', {
        detail: { incidentId, hasDrugPropertyPresent: false },
    }));
    window.dispatchEvent(new CustomEvent('report-nibrs-vehicle-presence-updated', {
        detail: { incidentId, hasVehiclePresent: false },
    }));
    window.dispatchEvent(new CustomEvent('report-nibrs-arrest-presence-updated', {
        detail: { incidentId, hasArrestPresent: false },
    }));
}
